import { useRef } from 'react';
import { bucketOfTask, resolveIslandState, stableAgentOrder, type IslandColor } from './agent-task-buckets';
import { useIslandQuickView } from './use-island-quickview';
import { usePeopleWaiting } from './use-people-waiting';
import { actionNeededSplit } from './action-needed-count';

export interface IslandState {
  /** green = resting (agents · skills), blue = agents at work, orange = waiting on you. */
  color: IslandColor;
  /** Things with a live run RIGHT NOW: tasks with `runInFlight === true` across the loaded account-wide
   *  list, PLUS the Maestro's own in-flight non-task actions (`maestroActions`). */
  workingCount: number;
  /** DISTINCT agents behind those — the blue pill's count. Missing-agent rows and maestro actions all
   *  fold into ONE Maestro bucket. Zero while the list is loading or when nothing carries a computed
   *  run state, which is exactly the "never blue from ignorance" rule. */
  workingAgents: number;
  /** The in-progress BUCKET count: the agent-started baseline and the island click BI read it, not the colour. */
  inMotionCount: number;
  /** The TASKS half of the orange pill: one per open QUESTION, plus one per waiting task that carries no
   *  question, plus the open asks (`buildIslandQuickView`'s `waitingCount`). One question blocking three tasks
   *  counts ONCE; the quick-view's Tasks section lists exactly these. */
  waitingCount: number;
  /** The PEOPLE half: the distinct NEEDS_YOU threads on the contact book head (`usePeopleWaiting`), 0 until the
   *  book loads (`native-apps/shared/outreach-needs-you/contract.json` rule 2). */
  peopleWaitingCount: number;
  /** `waitingCount + peopleWaitingCount` (`actionNeededSplit`) — what turns the pill orange. */
  waitingTotal: number;
  /** The waiting TASK total behind that number. Not shown anywhere — it is what the pill's BI keeps
   *  reporting as `waiting_count` (a 1:1 contract with the phones, the widgets and the Watch, which a
   *  redefinition would rewrite under), beside the new `waiting_questions`. */
  waitingTaskCount: number;
  /** The task-borne question cards inside `waitingCount` — the pill's BI `waiting_questions`. */
  waitingQuestionCards: number;
  /** The blue pill's leading glyphs — the distinct WORKING agents in activity order; `''` is the Maestro
   *  / agent-less bucket. Empty (`[]`) while the list is loading. */
  workingAgentIds: string[];
  /** The orange pill's leading glyphs — the distinct ACTION-REQUIRED agents in reading order; `''` is the
   *  Maestro / agent-less bucket. Empty (`[]`) while the list is loading. */
  waitingAgentIds: string[];
}

/**
 * The top-bar island's resolved state, off the SAME account-wide `/api/tasks` list the Tasks screen and its quick-view
 * render from, so the pill and the tab it opens can only disagree while a refetch is in flight.
 *
 * BLUE = agents whose loop is EXECUTING right now — computed client-side from the list's per-row
 * `runInFlight` verdict (web's equivalent of the aggregate's `totalWorking`/`workingAgents`; the phones
 * read those fields off the bridge). **Tri-state discipline**: a row with no `runInFlight` was never
 * computed and counts nothing, so an older server or a not-yet-loaded list falls through to orange/green
 * rather than asserting either way. Orange = waiting: ACTION_NEEDED tasks and open asks (`waitingCount`) PLUS
 * the people waiting for an answer (`peopleWaitingCount`, the NEEDS_YOU threads — an outreach-only question is
 * one of those now, never a task card: outreach-needs-you contract rule 1); a failed task is finished, not waiting on anyone, and lives on the
 * Failed tab (see `islandCountsFromRows`'s comment on why `failed` is excluded). While the read is still
 * in flight the counts are zero and the island rests green, exactly the pre-data state.
 *
 * `waitingCount` is read straight off `quickView.data.waitingCount` — the SAME action-required count the
 * orange quick-view card's own header states (`buildIslandQuickView`'s `isActionRequired` fold) — rather
 * than re-derived here, so the pill and the card it opens can never compute two different answers to the
 * same question. `inMotionCount` still buckets `sorted` itself: that list is already recency-windowed
 * (`sortedForTaskList`), so this reads `bucketOfTask` directly instead of re-filtering through `bucketTasks`.
 */
export function useIslandState(): IslandState {
  const quickView = useIslandQuickView();
  const working = quickView.status === 'ready'
    ? { workingCount: quickView.data.working.workingCount, workingAgents: quickView.data.working.workingAgents }
    : { workingCount: 0, workingAgents: 0 };
  const counts = quickView.status === 'ready'
    ? {
        inMotionCount: quickView.data.sorted.filter((task) => bucketOfTask(task) === 'inProgress').length,
        waitingCount: quickView.data.waitingCount,
        waitingTaskCount: quickView.data.waitingTaskCount,
        waitingQuestionCards: quickView.data.waitingQuestionCards,
      }
    : { inMotionCount: 0, waitingCount: 0, waitingTaskCount: 0, waitingQuestionCards: 0 };
  // The PEOPLE half (contract rule 2) — off the contact book head, not the tasks list. Rule 1's split: the pill
  // turns orange on the TOTAL, so a reader with nothing but a person waiting still sees "Waiting on you".
  // The `waitingCount` the builder states is already `questionCards + openAsks`, so it is the tasks input whole.
  const people = usePeopleWaiting();
  const split = actionNeededSplit(counts.waitingCount, 0, people.count);
  // The leading-glyph id sets, read straight off the same quick-view list as the counts (default [] until
  // it lands) — so the pill draws the actual working / action-needed agents, not the generic roster. The
  // sets are recency-first, so we hold each in a STABLE order for the lifetime of the state
  // (`stableAgentOrder`, idempotent + reset-on-empty): the dots keep their positions across the ~poll
  // refetch instead of re-sorting on every update, and a newly-working agent appears at the end.
  const workingOrder = useRef<string[]>([]);
  const waitingOrder = useRef<string[]>([]);
  const rawWorking = quickView.status === 'ready' ? quickView.data.workingAgentIds : [];
  const rawWaiting = quickView.status === 'ready' ? quickView.data.waitingAgentIds : [];
  workingOrder.current = stableAgentOrder(workingOrder.current, rawWorking);
  waitingOrder.current = stableAgentOrder(waitingOrder.current, rawWaiting);
  const agentIds = { workingAgentIds: workingOrder.current, waitingAgentIds: waitingOrder.current };
  return {
    color: resolveIslandState({ workingAgents: working.workingAgents, waitingCount: split.total }),
    ...working,
    ...counts,
    peopleWaitingCount: split.people,
    waitingTotal: split.total,
    ...agentIds,
  };
}
